import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { TrendingUp, Calculator, Loader2, Sparkles } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg rounded-lg p-3 border">
        <p className="font-medium text-slate-800">Year {payload[0].payload.year}</p>
        <p className="text-emerald-600">Balance: ${payload[0].value.toLocaleString()}</p>
        <p className="text-blue-500 text-sm">Interest: ${payload[0].payload.interest_earned.toLocaleString()}</p>
        <p className="text-slate-500 text-sm">Deposits: ${payload[0].payload.total_deposits.toLocaleString()}</p>
      </div>
    );
  }
  return null;
};

export const InterestCalculator = ({ currentBalance = 0, className }) => {
  const [principal, setPrincipal] = useState(currentBalance.toString());
  const [monthlyDeposit, setMonthlyDeposit] = useState('5');
  const [annualRate, setAnnualRate] = useState(5);
  const [years, setYears] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/interest`, {
        principal: parseFloat(principal) || 0,
        monthly_deposit: parseFloat(monthlyDeposit) || 0,
        annual_rate: annualRate,
        years: years
      });
      setResult(response.data);
    } catch (error) {
      console.error('Calculator error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={cn("shadow-lg rounded-2xl", className)} data-testid="interest-calculator">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Interest Calculator
        </CardTitle>
        <CardDescription>See how your savings can grow over time!</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="principal">Starting Amount ($)</Label>
            <Input
              id="principal"
              data-testid="calc-principal"
              type="number"
              min="0"
              step="0.01"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="monthly">Monthly Deposit ($)</Label>
            <Input
              id="monthly"
              data-testid="calc-monthly"
              type="number"
              min="0"
              step="0.01"
              value={monthlyDeposit}
              onChange={(e) => setMonthlyDeposit(e.target.value)}
              placeholder="5.00"
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <Label>Interest Rate</Label>
            <span className="text-emerald-600 font-semibold">{annualRate}% per year</span>
          </div>
          <Slider
            value={[annualRate]}
            onValueChange={(v) => setAnnualRate(v[0])}
            min={1}
            max={15}
            step={0.5}
            className="[&>span]:bg-emerald-500"
            data-testid="calc-rate-slider"
          />
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <Label>Time Period</Label>
            <span className="text-blue-600 font-semibold">{years} years</span>
          </div>
          <Slider
            value={[years]}
            onValueChange={(v) => setYears(v[0])}
            min={1}
            max={20}
            step={1}
            className="[&>span]:bg-blue-500"
            data-testid="calc-years-slider"
          />
        </div>

        <Button
          onClick={handleCalculate}
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          data-testid="calculate-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <TrendingUp className="w-4 h-4 mr-2" />
          )}
          Calculate Growth
        </Button>

        {/* Results */}
        {result && (
          <div className="space-y-4 pt-4 border-t">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-emerald-50 rounded-xl p-2 sm:p-3 text-center">
                <p className="text-[10px] sm:text-xs text-emerald-600 mb-1">Final Balance</p>
                <p className="text-sm sm:text-lg font-bold text-emerald-700 truncate" data-testid="result-final">
                  ${result.final_balance.toLocaleString()}
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl p-2 sm:p-3 text-center">
                <p className="text-[10px] sm:text-xs text-blue-600 mb-1">Interest</p>
                <p className="text-sm sm:text-lg font-bold text-blue-700 truncate" data-testid="result-interest">
                  ${result.total_interest.toLocaleString()}
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-2 sm:p-3 text-center">
                <p className="text-[10px] sm:text-xs text-purple-600 mb-1">Deposited</p>
                <p className="text-sm sm:text-lg font-bold text-purple-700 truncate" data-testid="result-deposits">
                  ${result.total_deposits.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Wow Message */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-amber-800 text-sm sm:text-base">
                  Your money could grow by ${(result.final_balance - result.total_deposits).toLocaleString()}!
                </p>
                <p className="text-xs sm:text-sm text-amber-600">
                  That's {((result.total_interest / result.total_deposits) * 100).toFixed(0)}% more than what you put in!
                </p>
              </div>
            </div>

            {/* Growth Chart */}
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={result.projections}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="year" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `Y${v}`}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorBalance)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InterestCalculator;
